import { useInfiniteQuery } from '@tanstack/react-query';
import { Platform } from 'react-native';
import { PaperData } from '../../components/papers/types';
import { getSessionAccessToken } from '../atomFeed';
import { normalizePaperData } from '../paperAuthors';
import { ProjectFeedFeedbackFilter, ProjectPaperFeedback } from '../projectPaperFeedback';
import { FeedPaper } from './useProjectFeed';

const DEFAULT_BASE_URL = 'https://paperzilla.ai';
const DEFAULT_LOCAL_API_BASE_URL = 'http://localhost:8001';
const GENERIC_SEARCH_ERROR = 'Unable to search all papers right now. Please try again.';
export const PROJECT_FEED_SEARCH_PAGE_SIZE = 20;

interface FeedSearchApiPaper extends PaperData {
    source?: {
        name?: string | null;
    } | null;
    venue_name?: string | null;
    reference_label?: string | null;
    markdown_ready?: boolean | null;
}

interface FeedSearchApiItem {
    id: string;
    short_id: string;
    slug: string;
    paper_title: string | null;
    summary: string | null;
    relevance_score: number | null;
    relevance_class: number | null;
    combined_score: number | null;
    matching_details: any;
    personalized_note: string | null;
    ready_at: string | null;
    feedback?: ProjectPaperFeedback | null;
    paper: FeedSearchApiPaper;
}

interface FeedSearchApiResponse {
    items: FeedSearchApiItem[];
    limit: number;
    offset: number;
    has_more: boolean;
    query: string;
}

export interface FeedSearchPage {
    papers: FeedPaper[];
    limit: number;
    offset: number;
    hasMore: boolean;
    query: string;
}

function trimTrailingSlash(url: string) {
    return url.replace(/\/+$/, '');
}

function getConfiguredBaseUrl() {
    return trimTrailingSlash(process.env.EXPO_PUBLIC_BASE_URL || DEFAULT_BASE_URL);
}

function isLocalWebDevHost() {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
        return false;
    }

    const host = window.location.hostname;
    return host === 'localhost' || host === '127.0.0.1';
}

function isSameOriginOnWeb(url: string) {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
        return false;
    }

    try {
        return new URL(url).origin === window.location.origin;
    } catch {
        return false;
    }
}

function getProjectFeedSearchEndpointCandidates(path: string) {
    const candidates: string[] = [];
    const configuredEndpoint = `${getConfiguredBaseUrl()}${path}`;
    const localEndpoint = `${DEFAULT_LOCAL_API_BASE_URL}${path}`;

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
        const sameOriginConfigured = isSameOriginOnWeb(configuredEndpoint);

        if (isLocalWebDevHost()) {
            if (sameOriginConfigured) {
                candidates.push(path);
            }
            candidates.push(localEndpoint);
            if (!sameOriginConfigured && !candidates.includes(configuredEndpoint)) {
                candidates.push(configuredEndpoint);
            }
            return candidates;
        }

        candidates.push(path);
        if (!sameOriginConfigured && !candidates.includes(configuredEndpoint)) {
            candidates.push(configuredEndpoint);
        }
        return candidates;
    }

    candidates.push(configuredEndpoint);
    return candidates;
}

async function parseErrorResponse(response: Response) {
    let message = `Request failed (${response.status})`;

    try {
        const body = await response.json();
        if (body?.detail) {
            message = String(body.detail);
        } else if (body?.message) {
            message = String(body.message);
        }
    } catch {
        try {
            const text = await response.text();
            if (text) {
                message = text;
            }
        } catch {
            // Keep default message.
        }
    }

    if (response.status === 401) {
        return 'Your session expired. Please sign in again.';
    }

    return message;
}

function mapSearchApiItem(item: FeedSearchApiItem): FeedPaper {
    return {
        id: item.id,
        short_id: item.short_id,
        slug: item.slug,
        paper_title: item.paper_title,
        summary: item.summary,
        relevance_score: item.relevance_score,
        relevance_class: item.relevance_class,
        matching_details: item.matching_details,
        personalized_note: item.personalized_note,
        ready_at: item.ready_at,
        paper: normalizePaperData(item.paper) as FeedSearchApiPaper,
        project_paper_feedback: item.feedback ?? null,
    };
}

async function requestProjectFeedSearchPage({
    projectId,
    query,
    feedbackFilter,
    mustRead,
    offset,
    signal,
}: {
    projectId: string;
    query: string;
    feedbackFilter: ProjectFeedFeedbackFilter;
    mustRead?: boolean | null;
    offset: number;
    signal?: AbortSignal;
}): Promise<FeedSearchPage> {
    const accessToken = await getSessionAccessToken();
    const params = new URLSearchParams({
        q: query,
        feedback_filter: feedbackFilter,
        limit: String(PROJECT_FEED_SEARCH_PAGE_SIZE),
        offset: String(offset),
    });

    if (mustRead !== null && mustRead !== undefined) {
        params.set('must_read', mustRead ? 'true' : 'false');
    }

    const path = `/api/projects/${encodeURIComponent(projectId)}/feed/search?${params.toString()}`;
    const endpoints = getProjectFeedSearchEndpointCandidates(path);
    let lastError: Error | null = null;

    for (let index = 0; index < endpoints.length; index += 1) {
        try {
            const response = await fetch(endpoints[index], {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                signal,
            });

            if (!response.ok) {
                const message = await parseErrorResponse(response);
                const hasMoreCandidates = index < endpoints.length - 1;
                const shouldRetry = hasMoreCandidates && (response.status === 404 || response.status === 405);

                if (shouldRetry) {
                    lastError = new Error(message);
                    continue;
                }

                throw new Error(message);
            }

            const payload = await response.json() as FeedSearchApiResponse;
            return {
                papers: payload.items.map(mapSearchApiItem),
                limit: payload.limit,
                offset: payload.offset,
                hasMore: payload.has_more,
                query: payload.query,
            };
        } catch (error: any) {
            if (error?.name === 'AbortError') {
                throw error;
            }

            if (error instanceof TypeError && index < endpoints.length - 1) {
                lastError = new Error('Could not reach the feed search endpoint.');
                continue;
            }

            lastError = error instanceof Error ? error : new Error(GENERIC_SEARCH_ERROR);
        }
    }

    throw lastError || new Error(GENERIC_SEARCH_ERROR);
}

export function useProjectFeedSearch(
    projectId: string | undefined,
    query: string,
    feedbackFilter: ProjectFeedFeedbackFilter,
    mustRead?: boolean | null,
    options: { enabled?: boolean } = {},
) {
    return useInfiniteQuery({
        queryKey: ['project-feed-search', projectId, query, feedbackFilter, mustRead ?? null],
        queryFn: async ({ pageParam = 0, signal }: { pageParam: number; signal?: AbortSignal }) => {
            if (!projectId) {
                throw new Error('No project ID');
            }

            return requestProjectFeedSearchPage({
                projectId,
                query,
                feedbackFilter,
                mustRead,
                offset: pageParam,
                signal,
            });
        },
        getNextPageParam: (lastPage) =>
            lastPage.hasMore ? lastPage.offset + lastPage.limit : undefined,
        initialPageParam: 0,
        enabled: Boolean(projectId) && query.trim().length >= 3 && (options.enabled ?? true),
        staleTime: 1000 * 60,
        gcTime: 1000 * 60 * 10,
    });
}
